import { useState, useEffect, useCallback } from 'react';
import { fetchAuditLogs, fetchUsers } from '../services/authService';
import { Shield, Search, RefreshCw, ChevronLeft, ChevronRight, LogIn, LogOut, UserPlus, UserCog, Key, CalendarDays, X } from 'lucide-react';

// ─── Action badge config ──────────────────────────────────────────────────────
const ACTION_CONFIG = {
  LOGIN_SUCCESS:          { label: 'Login',           color: 'bg-emerald-100 text-emerald-700', icon: LogIn    },
  LOGIN_FAILED:           { label: 'Login Failed',    color: 'bg-red-100 text-red-700',         icon: LogIn    },
  LOGOUT:                 { label: 'Logout',          color: 'bg-gray-100 text-gray-600',       icon: LogOut   },
  USER_CREATED:           { label: 'User Created',    color: 'bg-blue-100 text-blue-700',       icon: UserPlus },
  USER_UPDATED:           { label: 'User Updated',    color: 'bg-amber-100 text-amber-700',     icon: UserCog  },
  USER_DEACTIVATED:       { label: 'Deactivated',     color: 'bg-orange-100 text-orange-700',   icon: UserCog  },
  PASSWORD_RESET_SUCCESS: { label: 'Password Reset',  color: 'bg-purple-100 text-purple-700',   icon: Key      },
  FORGOT_PASSWORD_REQUESTED: { label: 'Forgot Pwd',  color: 'bg-indigo-100 text-indigo-700',   icon: Key      },
  TOKEN_REFRESHED:        { label: 'Token Refresh',   color: 'bg-slate-100 text-slate-600',     icon: RefreshCw},
};

function getActionConfig(action) {
  return ACTION_CONFIG[action] || { label: action, color: 'bg-gray-100 text-gray-600', icon: Shield };
}

export default function AuditLogs() {
  const [logs,       setLogs]       = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [search,     setSearch]     = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom,   setDateFrom]   = useState('');
  const [dateTo,     setDateTo]     = useState('');
  const [users,      setUsers]      = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  const loadLogs = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAuditLogs({
        page,
        limit: pagination.limit,
        action: search,
        user_id: userFilter,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setLogs(data.logs);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [search, userFilter, dateFrom, dateTo, pagination.limit]);

  useEffect(() => { loadLogs(1); }, [search, userFilter, dateFrom, dateTo]); // eslint-disable-line

  // Load users list for filter dropdown
  useEffect(() => {
    fetchUsers({ limit: 100 }).then(data => setUsers(data.users || [])).catch(() => {});
  }, []);

  const formatDate = (dateStr) => {
    // Backend always returns IST time via CONVERT_TZ — append +05:30 so the
    // browser parses it as IST regardless of the client's local timezone.
    const normalized = dateStr.replace(' ', 'T').replace(/(\.\d+)?Z?$/, '') + '+05:30';
    const d = new Date(normalized);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    }) + ' ' + d.toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: true,
    }).toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-sm text-gray-500 mt-1">{pagination.total} total events</p>
        </div>
        <button
          onClick={() => loadLogs(pagination.page)}
          className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Search + User Filter + Date Range */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-5 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Filter by action..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <select
          value={userFilter}
          onChange={e => setUserFilter(e.target.value)}
          className="px-3 cursor-pointer py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">All Users</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
          ))}
        </select>

        {/* Date range pickers */}
        <div className="flex items-center gap-1.5 border border-gray-300 rounded-lg bg-white px-3 py-1.5 focus-within:ring-2 focus-within:ring-emerald-500 focus-within:border-emerald-500 transition-all">
          <CalendarDays className="w-4 h-4 text-emerald-600 shrink-0" />
          <div className="flex items-center gap-1.5">
            <div className="flex flex-col">
              <span className="text-[10px] font-medium text-gray-400 leading-none mb-0.5">From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                max={dateTo || undefined}
                className="text-sm text-gray-700 bg-transparent border-none outline-none w-[118px] cursor-pointer"
                title="From date"
              />
            </div>
            <span className="text-gray-300 font-light text-base select-none">|</span>
            <div className="flex flex-col">
              <span className="text-[10px] font-medium text-gray-400 leading-none mb-0.5">To</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                className="text-sm text-gray-700 bg-transparent border-none outline-none w-[118px] cursor-pointer"
                title="To date"
              />
            </div>
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="ml-1 text-gray-400 hover:text-gray-600 transition-colors"
              title="Clear dates"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Quick date presets */}
        <div className="flex items-center gap-1.5">
          {[
            { label: 'Today',   days: 0  },
            { label: '7d',      days: 7  },
            { label: '30d',     days: 30 },
          ].map(({ label, days }) => {
            const today = new Date();
            const toStr = today.toISOString().slice(0, 10);
            const fromStr = days === 0
              ? toStr
              : new Date(today.setDate(today.getDate() - days)).toISOString().slice(0, 10);
            const isActive = dateFrom === fromStr && dateTo === toStr;
            return (
              <button
                key={label}
                onClick={() => { setDateFrom(fromStr); setDateTo(toStr); }}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  isActive
                    ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {(search || userFilter || dateFrom || dateTo) && (
          <button
            onClick={() => { setSearch(''); setUserFilter(''); setDateFrom(''); setDateTo(''); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Clear all
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Action</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Role</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date & Time (IST)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-12">
                    <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto" />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-gray-400">No audit logs found</td>
                </tr>
              ) : logs.map(log => {
                const cfg  = getActionConfig(log.action);
                const Icon = cfg.icon;
                return (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                        <Icon className="w-3 h-3" />
                        <span>{cfg.label}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {log.user_name ? (
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{log.user_name}</p>
                          <p className="text-xs text-gray-500 md:max-w-[160px] truncate">{log.user_email}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs italic">System / Unknown</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {log.user_role ? (
                        <span className="text-xs font-medium text-gray-600 capitalize">{log.user_role}</span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {formatDate(log.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} events)
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={pagination.page <= 1}
                onClick={() => loadLogs(pagination.page - 1)}
                className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => loadLogs(pagination.page + 1)}
                className="p-1.5 rounded border border-gray-300 disabled:opacity-40 hover:bg-white transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
